import { Validators } from '@angular/forms';
import { regex } from '../../../../common/regex.constants';
import { environment } from '../../../../../environments/environment';

export interface EmailModel {
  editValue: string;
  activationCode: string;
}

export interface PayloadModel {
  emails: [
    {
      emailType: string,
      emailAddress: string,
      emailVerified: boolean
    }
  ];
}

export interface VerifyCodeModel {
  purpose: string;
  email: string;
  sendNotification: boolean;
}

export class Email {
  public formBindings: EmailModel;
  constructor(
    public data?: EmailModel,
  ) { }

  /**
  * @method __defaults Return the defaults values for the model
  * @returns All the values in the EmailModel interface as ''
  **/
  public __defaults(): EmailModel {
    return this.formBindings = {
      editValue: '',
      activationCode: ''
    };
  }
  /**
  * @method values Return the values send in the data property
  * @returns SignUpModel Interface
  **/
  public get values(): EmailModel {
    return this.data !== undefined ? this.__formBindings({ ...this.data }) : this.__defaults();
  }
  /**
  * @method __formBindings Returns the values for initialize in case of read mode
  * @returns SignUpModel
  **/
  public __formBindings(bindings: EmailModel): EmailModel {
    return {
      'editValue': bindings['editValue'] ? bindings['editValue'] : '',
      'activationCode': bindings['activationCode'] ? bindings['activationCode'] : ''
    };
  }
  /**
  * @function tag returns the translation tag for email address when called
  * @returns {string}
  **/
  get tag(): string {
    return 'update-account.labels.email-address';
  }
  /**
  * @function endPoint returns the endpoint for updatting the email ID
  * @returns {string}
  **/
  get endPoint(): string {
    return environment.admin.apis.email_swap;
  }
  /**
  * @function placeholder returns the translation for email address when called
  * @returns {string}
  **/
  get placeholder(): string {
    return this.tag;
  }
  /**
  * @function translateParams returns the object for email address context when called
  * @returns {object}
  **/
  get translateParams(): object {
    return { 'tag': 'Email Address' };
  }

  /**
  * @method form Return the form controls to be used in the update-account component
  * @returns Object with the controls
  **/
  public get form(): object {
    return this.customerControls;
  }
  /**
  * @method customerControls Controls for the admin view
  * @returns Object with the required controls
  **/
  public get customerControls(): object {
    return {
      editValue: this.emailControl,
      activationCode: this.activationCodeControl
    };
  }
  /**
  * @method emailControl Controls for email field
  * @returns Array of validators
  **/
  public get emailControl(): any[] {
    return [
      { value: '' },
      [Validators.required,
      Validators.minLength(1),
      Validators.maxLength(64),
      Validators.pattern(regex.emailPattern)]
    ];
  }
  /**
  * @method activationCodeControl Controls for activationCode field
  * @returns Array of validators
  **/
  public get activationCodeControl(): any[] {
    return [
      { value: '' },
      [Validators.required,
      Validators.minLength(1),
      Validators.maxLength(6),
      Validators.pattern(regex.verificationCode)]
    ];
  }
  /**
  * @function fieldRequired returns the translation for required field validation error of email and code when called
  * @returns Array<string>
  **/
  get fieldRequired(): Array<string> {
    return ['sign-up.errors.email-required', 'sign-up.errors.code-required'];
  }
  /**
  * @function fieldPattern returns the translation for Pattern validation error of email and code when called
  * @returns Array<string>
  **/
  get fieldPattern(): Array<string> {
    return ['sign-up.validations.email-pattern', 'sign-up.validations.code-pattern'];
  }
  /**
  * @function fieldLength returns the translation for maxlength validation error of email and code when called
  * @returns Array<string>
  **/
  get fieldLength(): Array<string> {
    return ['sign-up.validations.email-maxlength', 'sign-up.validations.code-maxlength'];
  }
  /**
  * @method __formPayload Returns the email object when called
  * @returns PayloadModel
  **/
  public updateAccount_Payload(updatedMail: string): PayloadModel {
    return {
      emails: [
        {
          emailType: 'Azure',
          emailAddress: updatedMail,
          emailVerified: true
        }
      ]
    };
  }
  /**
  * @method verifyCode_Payload Returns the email object when called
  * @returns VerifyCodeModel
  **/
  public verifyCode_Payload(updatedMail: string, requestCode: boolean): VerifyCodeModel {
    return {
      purpose: requestCode ? 'REQUEST_CODE' : 'ACCOUNT_ACTIVATION',
      email: updatedMail,
      sendNotification: true
    };
  }

  /**
  * @method payload Return the payload object of email when called
  * @returns PayloadModel | VerifyCodeModel
  **/
  public payload(updatedMail: string, type: string, requestCode: boolean): PayloadModel | VerifyCodeModel {
    return type === 'update' ? this.updateAccount_Payload(updatedMail) : this.verifyCode_Payload(updatedMail, requestCode);
  }
}

